/**
 * @requirement S-041 — rewritten per C-03 §12 landing anatomy.
 *
 * Layout:
 *   [ app-bar: ProviderMenu (secondary) + placeholder for ThemeToggle ]
 *   [ Hero (wordmark + tagline) ]
 *   [ DropZone (primary CTA, visually dominant) ]
 *   [ FileSourceBadge (once a file is picked) ]
 *
 * Zero-network invariant preserved — all interaction stays in the
 * browser; provider integrations that would fetch are deferred to
 * post-sign-in flows.
 */

import { type ReactElement, useState } from 'react';

import { DropZone } from '../components/DropZone';
import { useDeviceStorage } from '../hooks/useDeviceStorage';
import { Hero } from './landing/Hero';
import { FileSourceBadge } from './landing/FileSourceBadge';
import { ProviderMenu } from './landing/ProviderMenu';

export function Landing(): ReactElement {
  const { openViaPicker, openFromDroppedFile } = useDeviceStorage();
  const [pickedName, setPickedName] = useState<string | null>(null);

  const handleFile = (file: File): void => {
    setPickedName(file.name);
    openFromDroppedFile(file);
  };

  return (
    <div className="min-h-screen bg-chrome-bg text-chrome-text-primary">
      {/* Header hosts navigation only on Landing; the identity
          (wordmark `<h1>` + tagline) lives in Hero within the main
          region to avoid duplicating "Recto" twice on the page. When
          the editor route ships, the header's empty left slot will
          host a `<Link to="/">Recto</Link>` for keyboard-nav home. */}
      <header className="flex items-center justify-end border-b border-chrome-border-subtle px-6 py-3">
        <ProviderMenu onOpenLocal={openViaPicker} />
      </header>
      <main
        className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-3xl flex-col items-center justify-center gap-8 px-6 text-center"
        aria-labelledby="landing-heading"
      >
        <Hero />
        <DropZone
          onFile={handleFile}
          onActivate={openViaPicker}
          ariaLabel="Drop a PDF here, or activate to browse for one"
        >
          <span className="font-medium">Drop a PDF here</span>
        </DropZone>
        {pickedName ? (
          <div className="flex flex-col items-center gap-2">
            <FileSourceBadge source="local" mode="guest" />
            <span className="text-sm text-chrome-text-secondary">{pickedName}</span>
          </div>
        ) : null}
      </main>
    </div>
  );
}
